import {
	ASSISTANCE_SERVICE_ERROR_CODES,
	STATUS_CODES,
} from '@atlassian/conversation-assistant-service-api/constants';
import {
	type FileUploadResponseItem,
	type FileValidationResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { isApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';

import { MAX_FILES } from './constants';
import messages from './messages';
/**
 * Add files up to the maximum number of files allowed.
 * @param addedFiles - The files to add.
 * @param currentFiles - The current files.
 * @returns The files to add.
 */
export const addFilesUpToMax = (
	addedFiles: File[],
	currentFiles: FileUploadResponseItem[],
): File[] => {
	const remainingSlots = MAX_FILES - currentFiles.length;
	const limitedFiles = addedFiles.slice(0, remainingSlots > 0 ? remainingSlots : 0);
	return limitedFiles;
};

export const mapValidateErrorCodeToMessage = (
	errorCode:
		| FileValidationResponse['validationResults'][number]['errorCode']
		| 'VALIDATION_RESULT_MISSING_ERROR_CODE'
		| 'VALIDATION_RESULT_NOT_FOUND',
): {
	id: string;
	defaultMessage: string;
	description: string;
} => {
	switch (errorCode) {
		case 'INVALID_CONTENT_TYPE':
			return messages.validationErrorInvalidContentType;
		case 'FILE_TOO_LARGE':
			return messages.validationErrorFileTooLarge;
		case 'FILE_NAME_MISSING':
			return messages.validationErrorFileNameMissing;
		case 'VALIDATION_RESULT_MISSING_ERROR_CODE':
			return messages.validationErrorMissingErrorCode;
		case 'VALIDATION_RESULT_NOT_FOUND':
			return messages.validationErrorNotFound;
		case 'UNKNOWN_ERROR':
		default:
			return messages.validationErrorUnknownError;
	}
};

/**
 * Checks if an error is a known/expected error type that should be treated as successful from experience tracking perspective.
 * @param error - The error object with errorAttributes.
 * @returns True if the error is a known error type, false otherwise.
 */
export const isKnownUploadFileUserError = (error: unknown): boolean => {
	if (!isApiError(error)) {
		return false;
	}

	return (
		error.errorAttributes.statusCode === STATUS_CODES.UNSUPPORTED_FILE_TYPE ||
		error.errorAttributes.errorCode === ASSISTANCE_SERVICE_ERROR_CODES.FILE_SIZE_TOO_LARGE ||
		error.errorAttributes.statusCode === STATUS_CODES.FILE_RAI_POLICY_VIOLATION
	);
};
